// Provider (tela Claude Design, artboard 14 e 21): da dove PopTown prende i modelli. Ogni tipologia
// si collega a modo suo: Locale (SurgeAI) con l'abbinamento, Codex con il codice dispositivo,
// Claude Code con il token dell'abbonamento. Il login dei CLI è di PopTown, non della macchina.
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import type {
  ConnectedProvider,
  PendingProvider,
  ProviderCheck,
  ProviderListing,
} from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { KindGroup, groupBy, type Tint } from '../../components/KindGroup';
import { PageHeader } from '../../components/PageHeader';
import { DefaultModelCard } from './DefaultModelCard';
import { useStore } from '../../store/StoreProvider';
import { AddProvider } from './AddProvider';
import { EditProvider } from './EditProvider';

const KIND_NAME: Record<string, string> = {
  'claude-code': 'Claude Code',
  codex: 'Codex',
  antigravity: 'Antigravity',
};

/** La tinta di ogni tipo, come sulla tela */
const TINT: Record<string, Tint> = {
  'claude-code': 'coral',
  codex: 'blue',
  antigravity: 'lilac',
  local: 'mint',
};

export function Providers() {
  const { dispatch, openModal } = useStore();
  const [openModels, setOpenModels] = useState<string | null>(null);
  const [checks, setChecks] = useState<Record<string, ProviderCheck | 'running'>>({});
  const [data, setData] = useState<ProviderListing | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ConnectedProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setData(await api.providers());
      // la prontezza la decide il server: dopo ogni cambio si rilegge
      dispatch({ type: 'auth/info', info: await api.sessionInfo() });
      setError(null);
    } catch {
      setError('Non riesco a leggere i provider.');
    }
  }, [dispatch]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const remove = (id: string, name: string, kind: string) =>
    openModal({
      kind: 'confirm',
      title: `Scollegare ${name}?`,
      okLabel: 'Scollega',
      danger: true,
      body:
        kind === 'local'
          ? 'La chiave va revocata anche in SurgeAI › Impostazioni › Assistenti.'
          : 'PopTown dimentica il login: per usarlo di nuovo andrà rifatto.',
      payload: {
        run: async () => {
          await api
            .removeProvider(id)
            .catch((e: unknown) =>
              setError(e instanceof ApiError && e.detail ? e.detail : 'Non riesco a scollegarlo.'),
            );
          await refresh();
        },
      },
    });

  // la prova chiede l'elenco dei modelli con la chiave dell'abbinamento
  const check = async (id: string) => {
    setChecks((c) => ({ ...c, [id]: 'running' }));
    const res = await api
      .testProvider(id)
      .catch((): ProviderCheck => ({ ok: false, latencyMs: 0, error: 'PopTown non risponde.' }));
    setChecks((c) => ({ ...c, [id]: res }));
    if (res.ok) await refresh();
  };

  return (
    <div className="page">
      <PageHeader
        title="Provider"
        subtitle="Da dove PopTown prende i modelli, raggruppato per tipo. Ogni tipologia si collega a modo suo."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
            Aggiungi provider
          </Button>
        }
      />
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Banner kind="danger">{error}</Banner>
        </div>
      )}
      {data && <DefaultModelCard providers={data.providers} />}
      {data && data.providers.length === 0 && data.pending.length === 0 && (
        <div
          style={{
            border: '1px dashed var(--border-strong)',
            borderRadius: 'var(--r-xl)',
            padding: 28,
            textAlign: 'center',
          }}
        >
          <div className="t-body" style={{ marginBottom: 6 }}>
            Nessun provider collegato.
          </div>
          <div className="t-small dim" style={{ marginBottom: 16 }}>
            Senza, nessuna richiesta parte: aggiungine uno.
          </div>
          <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
            Aggiungi provider
          </Button>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 20 }}>
        {groupBy(data?.providers ?? [], (p) => p.kind).map(([kind, items]) => {
          const models = items.reduce((n, p) => n + p.models.length, 0);
          const name = kind === 'local' ? 'Locali' : (KIND_NAME[kind] ?? kind);
          return (
            <KindGroup
              key={kind}
              title={name}
              tint={TINT[kind] ?? 'lilac'}
              initial={name.charAt(0).toUpperCase()}
              count={`${items.length} ${kind === 'local' ? 'server' : 'account'}`}
              summary={`${models} ${models === 1 ? 'modello' : 'modelli'} in tutto`}
            >
              {items.map((p) => (
                <article key={p.id} className="conn-card">
                  <div className="conn-head">
                    {/* sul telefono stato e pulsanti vanno sotto, invece di schiacciare il nome */}
                    <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                      <div className="conn-name">{p.name}</div>
                      <div className="conn-desc">
                        {p.kind === 'local' ? 'Chiave per applicazione' : 'Login di PopTown'} ·{' '}
                        {p.models.length} {p.models.length === 1 ? 'modello' : 'modelli'}
                      </div>
                      <CheckResult check={checks[p.id]} />
                    </div>
                    <span className="conn-badge">Collegato</span>
                    <Button
                      variant="secondary"
                      size="lg"
                      loading={checks[p.id] === 'running'}
                      onClick={() => void check(p.id)}
                    >
                      Prova
                    </Button>
                    <Button variant="secondary" size="lg" onClick={() => setEditing(p)}>
                      Modifica
                    </Button>
                    <button className="conn-unlink" onClick={() => remove(p.id, p.name, p.kind)}>
                      Scollega
                    </button>
                  </div>
                  {p.models.length > 0 && (
                    <ModelList
                      models={p.models}
                      open={openModels === p.id}
                      onToggle={() => setOpenModels(openModels === p.id ? null : p.id)}
                    />
                  )}
                </article>
              ))}
            </KindGroup>
          );
        })}
        {data?.pending.map((p: PendingProvider) => (
          <div
            key={p.id}
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-xl)',
              background: 'var(--panel)',
              padding: 16,
            }}
          >
            <div className="t-body" style={{ fontWeight: 600 }}>
              {p.kind === 'local' ? p.url : (p.name ?? KIND_NAME[p.kind] ?? p.kind)} · in attesa di
              approvazione, codice <code>{p.userCode}</code>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <Button variant="secondary" onClick={() => setAdding(true)}>
                Continua
              </Button>
              <Button variant="ghost" onClick={() => void api.cancelProvider(p.id).then(refresh)}>
                Annulla
              </Button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <EditProvider
          provider={editing}
          onClose={(changed) => {
            setEditing(null);
            if (changed) void refresh();
          }}
        />
      )}
      {adding && data && (
        <AddProvider
          kinds={data.kinds}
          resume={data.pending[0]}
          onClose={() => {
            setAdding(false);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function CheckResult({ check }: { check?: ProviderCheck | 'running' }) {
  if (!check || check === 'running') return null;
  return (
    <div
      className="t-caption"
      style={{ marginTop: 4, color: check.ok ? 'var(--ok)' : 'var(--danger)' }}
    >
      {check.ok
        ? `Risponde in ${check.latencyMs} ms · ${check.models?.length ?? 0} modelli`
        : check.error}
    </div>
  );
}

// i modelli del provider: chiusi di default, aperti come chip nella tinta del tipo (tela)
function ModelList({
  models,
  open,
  onToggle,
}: {
  models: string[];
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <button className="conn-toggle" aria-expanded={open} onClick={onToggle}>
        <span
          style={{
            display: 'grid',
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform var(--dur-2)',
          }}
        >
          <Icon name="chevron-right" size={16} />
        </span>
        {open ? 'Nascondi i modelli' : `Mostra i ${models.length} modelli`}
      </button>
      {open && (
        <ul className="conn-models">
          {models.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
    </>
  );
}
